import type { Form, FormField } from '../interfaces/form.interface';
import { FormType } from '../interfaces/form.interface';
import { DropdownData } from '../interfaces/input.interface';
import { ReactiveDropdownOption, ReactiveFormValueUtils } from './reactive-form-value.utils';

export type ReactiveFormDefaultValueContext<T> = {
    form: Form<T>;
    previousData: T | null;
    dropdownsFinalized: boolean;
};

export class ReactiveFormDefaultValueResolver<T> {
    public resolve(field: FormField<T>, context: ReactiveFormDefaultValueContext<T>): unknown {
        if (ReactiveFormValueUtils.isRepeatableField(field)) {
            return [];
        }

        const isMulti = ReactiveFormValueUtils.isMultiSelectField(field);

        if (context.form.type === FormType.DUPLICATION && field.conditions.isRememberedOnDuplicate === false) {
            return isMulti ? [] : '';
        }

        const previousValue = this.getPreviousValue(field, context.previousData);
        const hasPreviousValue = previousValue !== undefined &&
            previousValue !== null &&
            !(typeof previousValue === 'string' && ReactiveFormValueUtils.isBadDisplayToken(previousValue));

        if (hasPreviousValue) {
            return this.resolveKnownValue(field, previousValue, isMulti, context.dropdownsFinalized);
        }

        if (field.input?.defaultValue === null || field.input?.defaultValue === undefined) {
            return isMulti ? [] : '';
        }

        return this.resolveKnownValue(field, field.input.defaultValue, isMulti, context.dropdownsFinalized);
    }

    private getPreviousValue(field: FormField<T>, previousData: T | null): unknown {
        const record = previousData as Record<string, unknown> | null;

        if (!record) {
            return undefined;
        }

        const key = field.name ?? '';
        const aliasKey = key.endsWith('_nombres') ? `ids_${key.replace(/_nombres$/, '')}` : undefined;

        return record[key] ?? (aliasKey ? record[aliasKey] : undefined);
    }

    private resolveKnownValue(
        field: FormField<T>,
        rawValue: unknown,
        isMulti: boolean,
        dropdownsFinalized: boolean,
    ): unknown {
        if (isMulti) {
            return this.resolveMultiValue(field, rawValue, dropdownsFinalized);
        }

        if (field.input?.dropdownConfiguration?.valuePrimaryKey) {
            return this.resolveSingleDropdownValue(field, rawValue, dropdownsFinalized);
        }

        if (typeof rawValue === 'string' && ReactiveFormValueUtils.isBadDisplayToken(rawValue)) {
            return '';
        }

        return rawValue;
    }

    private resolveMultiValue(field: FormField<T>, rawValue: unknown, dropdownsFinalized: boolean): unknown[] {
        const items = this.parseMultiValue(rawValue);
        const primaryKey = ReactiveFormValueUtils.getFieldPrimaryKey(field);
        const looksLikeObjects = items.length > 0 && items.every((item) => {
            if (typeof item !== 'object' || item === null) {
                return false;
            }

            return (item as Record<string, unknown>)[primaryKey] !== null && (item as Record<string, unknown>)[primaryKey] !== undefined;
        });

        if (looksLikeObjects) {
            return items;
        }

        const ids = items
            .map((item) => ReactiveFormValueUtils.extractComparableId(item, primaryKey))
            .filter((item): item is string | number => item !== null);

        const options = this.getOptions(field);
        const mappedValues = ids
            .map((id) => ReactiveFormValueUtils.mapIdToOptionValue(id, options, primaryKey))
            .filter((item) => item !== null);

        if (mappedValues.length > 0) {
            return mappedValues;
        }

        if (dropdownsFinalized) {
            return [];
        }

        return ids;
    }

    private resolveSingleDropdownValue(field: FormField<T>, rawValue: unknown, dropdownsFinalized: boolean): unknown {
        const primaryKey = ReactiveFormValueUtils.getFieldPrimaryKey(field);

        if (this.hasObjectPrimaryKey(rawValue, primaryKey)) {
            return rawValue;
        }

        if (typeof rawValue === 'string' && ReactiveFormValueUtils.isBadDisplayToken(rawValue)) {
            return '';
        }

        const id = ReactiveFormValueUtils.extractComparableId(rawValue, primaryKey);
        const mappedValue = id === null ? null : ReactiveFormValueUtils.mapIdToOptionValue(id, this.getOptions(field), primaryKey);

        if (mappedValue !== null) {
            return mappedValue;
        }

        if (dropdownsFinalized) {
            return '';
        }

        return rawValue;
    }

    private hasObjectPrimaryKey(value: unknown, primaryKey: string): boolean {
        if (typeof value !== 'object' || value === null) {
            return false;
        }

        const primaryKeyValue = (value as Record<string, unknown>)[primaryKey];

        return primaryKeyValue !== null && primaryKeyValue !== undefined;
    }

    private parseMultiValue(rawValue: unknown): unknown[] {
        if (Array.isArray(rawValue)) {
            return rawValue;
        }

        if (typeof rawValue !== 'string' || rawValue.length === 0 || ReactiveFormValueUtils.isBadDisplayToken(rawValue)) {
            return [];
        }

        try {
            const parsed = JSON.parse(rawValue) as unknown;

            if (Array.isArray(parsed)) {
                return parsed;
            }
        } catch {
            // Plain serialized ids are handled below.
        }

        return ReactiveFormValueUtils.splitSerializedIds(rawValue);
    }

    private getOptions(field: FormField<T>): ReactiveDropdownOption[] {
        const dropdownData = field.input?.data as DropdownData<unknown> | undefined;

        return ReactiveFormValueUtils.getDropdownOptions(dropdownData);
    }
}
